/**
 * File Explorer wire schemas — mirror chaos-file-explorer's Python
 * responses. The gateway only proxies; these schemas are the canonical
 * contract for both the gateway's request validation and the SPA's
 * fetch wrappers.
 *
 * Field names are snake_case because the FastAPI service emits them
 * that way (Pydantic default). The frontend wrapper layer (`lib/api/fs.ts`
 * in chaos-ui) translates to camelCase before passing to components, so
 * consumers stay TypeScript-idiomatic.
 */
import { z } from 'zod';

// ── Node ───────────────────────────────────────────────────────────────

export const fsNodeKindSchema = z.enum(['dir', 'file', 'symlink', 'other', 'unknown']);
export type FsNodeKind = z.infer<typeof fsNodeKindSchema>;

// `fsTreeNodeSchema` is the canonical node returned by /fs/list, /fs/stat
// and /fs/upload. It's recursive: a directory listing returns its children
// inline so the UI can paint one level without a second round-trip.
//
// The recursive type is declared explicitly to satisfy
// `exactOptionalPropertyTypes` — `z.infer` widens optional fields to
// include `undefined`, which TypeScript would otherwise refuse to assign
// onto a typed array.
export interface FsTreeNode {
  name: string;
  path: string;
  kind: FsNodeKind;
  size_bytes?: number | null | undefined;
  mime?: string | null | undefined;
  modified_at?: string | null | undefined;
  truncated?: boolean | undefined;
  is_symlink?: boolean | undefined;
  symlink_target?: string | null | undefined;
  children?: FsTreeNode[] | undefined;
}

export const fsTreeNodeSchema: z.ZodType<FsTreeNode> = z.lazy(() =>
  z.object({
    name: z.string(),
    path: z.string(),
    kind: fsNodeKindSchema,
    size_bytes: z.number().int().nonnegative().nullable().optional(),
    mime: z.string().nullable().optional(),
    modified_at: z.string().nullable().optional(),
    truncated: z.boolean().optional(),
    is_symlink: z.boolean().optional(),
    symlink_target: z.string().nullable().optional(),
    children: fsTreeNodeSchema.array().optional(),
  }),
);

// ── Read ───────────────────────────────────────────────────────────────

export const fsReadKindSchema = z.enum(['markdown', 'code', 'image', 'text', 'binary', 'stub']);
export type FsReadKind = z.infer<typeof fsReadKindSchema>;

export const fsReadResponseSchema = z.object({
  path: z.string(),
  name: z.string(),
  size_bytes: z.number().int().nonnegative(),
  bytes_read: z.number().int().nonnegative(),
  offset_bytes: z.number().int().nonnegative(),
  eof: z.boolean(),
  mime: z.string().nullable(),
  kind: fsReadKindSchema,
  text: z.string(),
  masked: z.boolean(),
});
export type FsReadResponse = z.infer<typeof fsReadResponseSchema>;

// ── Search ─────────────────────────────────────────────────────────────

export const fsSearchHitSchema = z.object({
  path: z.string(),
  line: z.number().int().positive().nullable().optional(),
  snippet: z.string().nullable().optional(),
  matched_in: z.enum(['content', 'filename']),
});
export type FsSearchHit = z.infer<typeof fsSearchHitSchema>;

export const fsSearchResponseSchema = z.object({
  query: z.string(),
  scope: z.string().nullable().optional(),
  hits: fsSearchHitSchema.array(),
  truncated: z.boolean(),
});
export type FsSearchResponse = z.infer<typeof fsSearchResponseSchema>;

// ── Scopes ─────────────────────────────────────────────────────────────

export const fsScopesResponseSchema = z.object({
  scopes: z.string().array(),
});
export type FsScopesResponse = z.infer<typeof fsScopesResponseSchema>;

// ── Legacy `chaos.fs_scopes` row (kept for Phase 5+ DB-backed scopes). ─
export const fsScopeSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  label: z.string().min(1).max(80),
  basePath: z
    .string()
    .min(1)
    .max(512)
    .regex(/^\/(?:[^./][^/]*\/?)*$/, 'must be an absolute path without ".." traversal'),
  readOnly: z.boolean(),
  archivedAt: z.string().datetime().nullable(),
});
export type FsScope = z.infer<typeof fsScopeSchema>;

// ── Pin (legacy schema, used by /fs/pin) ───────────────────────────────

export const fsPinSchema = z.object({
  id: z.string().uuid(),
  path: z.string(),
  label: z.string().nullable().optional(),
  created_at: z.string(),
});
export type FsPin = z.infer<typeof fsPinSchema>;
